import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import { API } from '../../utils/config';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  GitCompare, 
  Check, 
  X, 
  AlertTriangle,
  ArrowLeft,
  Home,
  ChevronDown,
  ChevronUp,
  Image,
  MessageSquare,
  Clock
} from 'lucide-react';


const SchedaQualitaConfronto = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('hw_token');
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [showOnlyDifferences, setShowOnlyDifferences] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API}/api/quality-sheets/property/${propertyId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (res.ok) {
          const result = await res.json();
          setData(result);
          
          // Espandi tutte le sezioni
          if (result.ufficio_sheet?.sections) {
            const expanded = {};
            result.ufficio_sheet.sections.forEach(s => { expanded[s.id] = true; });
            setExpandedSections(expanded);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [propertyId, token]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const getItemStatus = (ufItem, respItem) => {
    const ufValue = ufItem?.checked;
    const respValue = respItem?.checked;
    
    if (ufValue === null || ufValue === undefined || respValue === null || respValue === undefined) {
      return 'incomplete';
    }
    if (ufValue === respValue) {
      return ufValue ? 'agree_ok' : 'agree_problem';
    }
    return 'disagree';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 text-center">
        <p className="text-slate-500">Dati non disponibili</p>
      </div>
    );
  }

  const { ufficio_sheet, responsabile_sheet, comparison, property_name } = data;

  // Se manca una delle due schede
  if (!ufficio_sheet || !responsabile_sheet) {
    return (
      <div className="p-4 md:p-6 space-y-4 max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold">Confronto Schede Qualità</h1>
            <p className="text-sm text-slate-500">{property_name}</p>
          </div>
        </div>
        
        <Card className="p-8 text-center">
          <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-amber-500" />
          <h2 className="text-lg font-medium mb-2">Confronto non disponibile</h2>
          <p className="text-slate-500 mb-4">
            {!ufficio_sheet && "Manca la scheda compilata dall'ufficio."}
            {!responsabile_sheet && "Manca la scheda compilata dal responsabile."}
          </p>
          <Button onClick={() => navigate(-1)}>Torna indietro</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <GitCompare className="h-6 w-6 text-purple-600" />
              Confronto Schede Qualità
            </h1>
            <p className="text-sm text-slate-500 flex items-center gap-1">
              <Home className="h-4 w-4" />
              {property_name}
            </p>
          </div>
        </div>
        
        <Button
          variant={showOnlyDifferences ? "default" : "outline"}
          onClick={() => setShowOnlyDifferences(!showOnlyDifferences)}
        >
          <AlertTriangle className="h-4 w-4 mr-2" />
          {showOnlyDifferences ? "Mostra tutto" : "Solo differenze"}
        </Button>
      </div>

      {/* Riepilogo confronto */}
      {comparison && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className={`border-2 ${comparison.agreement_score >= 80 ? 'border-green-300 bg-green-50' : comparison.agreement_score >= 50 ? 'border-amber-300 bg-amber-50' : 'border-red-300 bg-red-50'}`}>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold">{comparison.agreement_score}%</div>
              <div className="text-sm text-slate-600">Accordo</div>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{comparison.agreements}</div>
              <div className="text-sm text-green-700">Voci concordi</div>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-red-600">{comparison.total_differences}</div>
              <div className="text-sm text-red-700">Differenze</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-50 border-slate-200">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-slate-600">{comparison.total_compared}</div>
              <div className="text-sm text-slate-600">Voci confrontate</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Info compilatori */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-amber-600">Ufficio</Badge>
              <span className="font-medium">{ufficio_sheet.compiler_email}</span>
            </div>
            <div className="text-sm text-slate-600 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              {ufficio_sheet.signature ? (
                <span>Firmata: {new Date(ufficio_sheet.signature.signed_at).toLocaleString('it-IT')}</span>
              ) : (
                <span>Ultima modifica: {new Date(ufficio_sheet.updated_at).toLocaleString('it-IT')}</span>
              )}
            </div>
            <div className="text-sm mt-1">
              Punteggio: <strong>{ufficio_sheet.quality_score}%</strong> 
              ({ufficio_sheet.checked_ok} OK, {ufficio_sheet.checked_problems} problemi)
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-blue-600">Responsabile</Badge>
              <span className="font-medium">{responsabile_sheet.compiler_email}</span>
            </div>
            <div className="text-sm text-slate-600 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              {responsabile_sheet.signature ? (
                <span>Firmata: {new Date(responsabile_sheet.signature.signed_at).toLocaleString('it-IT')}</span>
              ) : (
                <span>Ultima modifica: {new Date(responsabile_sheet.updated_at).toLocaleString('it-IT')}</span>
              )}
            </div>
            <div className="text-sm mt-1">
              Punteggio: <strong>{responsabile_sheet.quality_score}%</strong>
              ({responsabile_sheet.checked_ok} OK, {responsabile_sheet.checked_problems} problemi)
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Confronto sezioni */}
      {ufficio_sheet.sections.map((ufSection, sectionIdx) => {
        const respSection = responsabile_sheet.sections[sectionIdx];
        
        // Conta differenze nella sezione
        let sectionDiffs = 0;
        ufSection.items.forEach((ufItem, itemIdx) => {
          const respItem = respSection?.items[itemIdx];
          if (getItemStatus(ufItem, respItem) === 'disagree') {
            sectionDiffs++;
          }
        });
        
        // Se mostra solo differenze e non ce ne sono, salta
        if (showOnlyDifferences && sectionDiffs === 0) {
          return null;
        }
        
        return (
          <Card key={ufSection.id} className="overflow-hidden">
            <CardHeader 
              className="cursor-pointer bg-slate-50 hover:bg-slate-100 transition-colors py-3"
              onClick={() => toggleSection(ufSection.id)}
            >
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-medium">{ufSection.title}</CardTitle>
                <div className="flex items-center gap-2">
                  {sectionDiffs > 0 && (
                    <Badge className="bg-red-100 text-red-800">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      {sectionDiffs} differenze
                    </Badge>
                  )}
                  {expandedSections[ufSection.id] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </div>
              </div>
            </CardHeader>
            
            {expandedSections[ufSection.id] && (
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="bg-slate-100 text-xs">
                    <tr>
                      <th className="text-left p-2 w-1/3">Voce</th>
                      <th className="text-center p-2 w-1/3 bg-amber-50">Ufficio</th>
                      <th className="text-center p-2 w-1/3 bg-blue-50">Responsabile</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {ufSection.items.map((ufItem, itemIdx) => {
                      const respItem = respSection?.items[itemIdx];
                      const status = getItemStatus(ufItem, respItem);
                      
                      // Se mostra solo differenze e non è una differenza, salta
                      if (showOnlyDifferences && status !== 'disagree') {
                        return null;
                      }
                      
                      const rowBg = status === 'disagree' ? 'bg-red-50' : 
                                    status === 'agree_problem' ? 'bg-amber-50/50' : '';
                      
                      return (
                        <tr key={ufItem.id} className={rowBg}>
                          <td className="p-2 text-sm">
                            {ufItem.label}
                            {status === 'disagree' && (
                              <AlertTriangle className="h-4 w-4 text-red-500 inline ml-2" />
                            )}
                          </td>
                          
                          {/* Colonna Ufficio */}
                          <td className="p-2 text-center bg-amber-50/30">
                            <div className="flex flex-col items-center gap-1">
                              {ufItem.checked === true && (
                                <Badge className="bg-green-100 text-green-800">
                                  <Check className="h-3 w-3 mr-1" /> OK
                                </Badge>
                              )}
                              {ufItem.checked === false && (
                                <Badge className="bg-red-100 text-red-800">
                                  <X className="h-3 w-3 mr-1" /> Problema
                                </Badge>
                              )}
                              {ufItem.checked === null && (
                                <Badge variant="outline" className="text-slate-400">-</Badge>
                              )}
                              {ufItem.note && (
                                <div className="text-xs text-slate-600 flex items-center gap-1 mt-1">
                                  <MessageSquare className="h-3 w-3" />
                                  <span className="truncate max-w-[150px]">{ufItem.note}</span>
                                </div>
                              )}
                              {ufItem.photos?.length > 0 && (
                                <div className="flex gap-1 mt-1">
                                  {ufItem.photos.map((photo, idx) => (
                                    <img 
                                      key={idx}
                                      src={photo}
                                      alt=""
                                      className="h-8 w-8 object-cover rounded cursor-pointer"
                                      onClick={() => window.open(photo, '_blank')}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                          
                          {/* Colonna Responsabile */}
                          <td className="p-2 text-center bg-blue-50/30">
                            <div className="flex flex-col items-center gap-1">
                              {respItem?.checked === true && (
                                <Badge className="bg-green-100 text-green-800">
                                  <Check className="h-3 w-3 mr-1" /> OK
                                </Badge>
                              )}
                              {respItem?.checked === false && (
                                <Badge className="bg-red-100 text-red-800">
                                  <X className="h-3 w-3 mr-1" /> Problema
                                </Badge>
                              )}
                              {(respItem?.checked === null || respItem?.checked === undefined) && (
                                <Badge variant="outline" className="text-slate-400">-</Badge>
                              )}
                              {respItem?.note && (
                                <div className="text-xs text-slate-600 flex items-center gap-1 mt-1">
                                  <MessageSquare className="h-3 w-3" />
                                  <span className="truncate max-w-[150px]">{respItem.note}</span>
                                </div>
                              )}
                              {respItem?.photos?.length > 0 && (
                                <div className="flex gap-1 mt-1">
                                  {respItem.photos.map((photo, idx) => (
                                    <img 
                                      key={idx}
                                      src={photo}
                                      alt=""
                                      className="h-8 w-8 object-cover rounded cursor-pointer"
                                      onClick={() => window.open(photo, '_blank')}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            )}
          </Card>
        );
      })}

      {/* Note generali */}
      {(ufficio_sheet.general_notes || responsabile_sheet.general_notes) && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-base font-medium">Note Generali</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-amber-50 p-3 rounded-lg">
                <div className="font-medium text-amber-800 mb-2">Ufficio:</div>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">
                  {ufficio_sheet.general_notes || <span className="text-slate-400 italic">Nessuna nota</span>}
                </p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="font-medium text-blue-800 mb-2">Responsabile:</div>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">
                  {responsabile_sheet.general_notes || <span className="text-slate-400 italic">Nessuna nota</span>}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SchedaQualitaConfronto;
